import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, RefreshCw, Trash2, Eye, FileText, Download, Users, Award, TrendingUp, CheckCircle, XCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Level thresholds - IMPORTANT: These define the grading scale
const LEVEL_THRESHOLDS = [
  { 
    level: 'Foreman', 
    min: 180, 
    max: 184, 
    color: 'bg-purple-500', 
    shortDesc: 'Coordonator de șantier / responsabil zonă',
    responsibilities: [
      'Coordonarea mai multor echipe',
      'Organizarea logisticii',
      'Relație directă cu clientul',
      'Verificarea conformității cu planurile',
      'Control documentație (SJA, controale)',
      'Monitorizarea timpului și resurselor',
      'Aprobare operațională overtime'
    ],
    accountableFor: [
      'Rezultatul final al proiectului',
      'Siguranța generală',
      'Imaginea firmei'
    ],
    note: 'Foreman participă activ în muncă atunci când este necesar.'
  },
  { 
    level: 'Team Leader', 
    min: 146, 
    max: 179, 
    color: 'bg-blue-500', 
    shortDesc: 'Lider operațional ACTIV – lucrează fizic și coordonează',
    principle: 'TL este în primul rând montator activ, și în al doilea rând coordonator.',
    responsibilities: [
      'Participă direct la montaj/demontaj (min 70% din timp)',
      'Dă exemplu prin ritm și disciplină',
      'Distribuie sarcini zilnic',
      'Monitorizează progresul și corectează greșeli',
      'Se asigură că echipa respectă planul',
      'Raportează zilnic către Project Leader'
    ],
    accountableFor: [
      'Ritmul echipei',
      'Respectarea termenelor',
      'Calitate execuție',
      'Respectarea HSE'
    ],
    notAllowed: [
      'Staționare în mașină fără motiv operațional',
      'Delegare totală a muncii',
      'Pauze excesive'
    ]
  },
  { 
    level: 'Level 3 (Senior)', 
    min: 120, 
    max: 145, 
    color: 'bg-green-500', 
    shortDesc: 'Specialist tehnic de șantier',
    responsibilities: [
      'Montaj structuri complexe',
      'Interpretare planuri tehnice avansate',
      'Ajustări tehnice pe teren',
      'Verificarea ancorărilor și stabilității',
      'Instruirea Level 1 și 2',
      'Control tehnic pe echipă'
    ],
    accountableFor: [
      'Corectitudinea tehnică a execuției',
      'Siguranța echipei în zona sa'
    ]
  },
  { 
    level: 'Level 2 (Experienced)', 
    min: 70, 
    max: 119, 
    color: 'bg-yellow-500', 
    shortDesc: 'Montator independent',
    responsibilities: [
      'Montaj/demontaj conform planului',
      'Citirea planurilor simple',
      'Asigurarea stabilității structurii',
      'Sprijinirea Level 1',
      'Respectarea standardelor tehnice și HSE'
    ],
    accountableFor: [
      'Calitatea propriei lucrări',
      'Siguranța zonei unde lucrează'
    ]
  },
  { 
    level: 'Level 1 (Junior)', 
    min: 0, 
    max: 69, 
    color: 'bg-orange-500', 
    shortDesc: 'Montator în formare, lucrează sub supraveghere directă',
    responsibilities: [
      'Montaj/demontaj sub coordonare',
      'Manipulare materiale',
      'Respectarea strictă a instrucțiunilor primite',
      'Utilizare corectă EIP (PPE)',
      'Menținerea curățeniei și ordinii',
      'Raportarea pericolelor sau problemelor'
    ],
    notAllowed: [
      'Ia decizii tehnice',
      'Modifice structuri fără aprobare',
      'Coordoneze alți muncitori'
    ]
  },
];

// Question definitions for displaying answers
const QUESTIONS_MAP = {
  // Section 1: Technical competence
  tech_draw: {
    section: 'Technical competence',
    question: 'Can you build independently based on drawings/instructions?',
    options: { 12: 'Yes, fully independently', 6: 'Yes, but need help sometimes', 0: 'No (need constant supervision)' }
  },
  tech_complex: {
    section: 'Technical competence',
    question: 'Complex builds (stairs/roofs/cantilever/heavy duty):',
    options: { 14: 'Very good (plan & execute)', 10: 'Good (most cases)', 5: 'Basic (simple tasks only)', 0: 'Not comfortable' }
  },
  tech_problem: {
    section: 'Technical competence',
    question: 'When something does not fit on site (obstacles/changes), you:',
    options: { 12: 'Solve within plan + report deviations immediately', 6: 'Suggest options to TL/Foreman', 0: 'Wait for others to decide' }
  },
  // Section 2: HSE / HMS & SJA
  hse_sja: {
    section: 'HSE / HMS & SJA',
    question: 'SJA / toolbox talk:',
    options: { 14: 'I can lead SJA for the team', 8: 'I participate and follow', 0: 'I need help / don\'t know routine' }
  },
  hse_ppe: {
    section: 'HSE / HMS & SJA',
    question: 'PPE / site rules compliance:',
    options: { 10: 'I enforce it in the team', 6: 'Consistent compliance', 0: 'Sometimes forget / need reminders' }
  },
  hse_stop: {
    section: 'HSE / HMS & SJA',
    question: 'If you see unsafe work:',
    options: { 10: 'I stop work and correct immediately', 6: 'I inform TL/Foreman', 0: 'I ignore / assume someone else handles it' }
  },
  // Section 3: Quality control
  q_checks: {
    section: 'Quality control',
    question: 'Before inspection/handover you check anchoring, guardrails, access, stability:',
    options: { 12: 'Always', 6: 'Sometimes', 0: 'Rarely' }
  },
  q_dev: {
    section: 'Quality control',
    question: 'If you discover a deviation:',
    options: { 10: 'Fix immediately + report', 6: 'Report it', 0: 'Leave it for later' }
  },
  // Section 4: Leadership
  lead_team: {
    section: 'Leadership',
    question: 'Have you guided other scaffolders during work?',
    options: { 14: 'Yes, regularly (small team)', 8: 'Yes, 1–2 people sometimes', 0: 'Never' }
  },
  lead_tasks: {
    section: 'Leadership',
    question: 'Task distribution:',
    options: { 12: 'I plan daily tasks and follow up', 6: 'I can assign simple tasks', 0: 'I prefer others to assign tasks' }
  },
  // Section 5: Client communication
  cl_comm: {
    section: 'Client communication',
    question: 'Client communication on site:',
    options: { 12: 'Often (planning & coordination)', 6: 'Sometimes (simple questions)', 0: 'No' }
  },
  cl_rep: {
    section: 'Client communication',
    question: 'Reporting progress, risks, deviations:',
    options: { 10: 'I report early and proactively', 5: 'I report when asked', 0: 'I rarely report' }
  },
  // Section 6: Materials & equipment
  mat_order: {
    section: 'Materials & equipment',
    question: 'Work area & material handling:',
    options: { 10: 'Strict: clean, counted, returned', 6: 'Usually clean and organized', 0: 'Often messy / not my focus' }
  },
  mat_loss: {
    section: 'Materials & equipment',
    question: 'If materials/tools are missing or damaged:',
    options: { 10: 'Report immediately + prevent repeat', 6: 'Inform TL/warehouse', 0: 'Ignore / hope it\'s found later' }
  },
  // Section 7: Productivity
  prod_deadline: {
    section: 'Productivity',
    question: 'Deadlines & pace:',
    options: { 12: 'I plan work to hit deadlines', 6: 'I usually manage', 0: 'I struggle often' }
  },
  prod_att: {
    section: 'Productivity',
    question: 'Attendance / punctuality / professionalism:',
    options: { 10: 'I set the standard for others', 6: 'Stable and professional', 0: 'Needs improvement' }
  }
};

// Level colors
const getLevelColor = (level) => {
  if (!level) return 'bg-gray-500';
  if (level.includes('Foreman') || level.includes('Formann') || level.includes('Sef')) return 'bg-purple-500';
  if (level.includes('Team Leader') || level.includes('Teamleder')) return 'bg-blue-500';
  if (level.includes('Level 3') || level.includes('Nivel 3') || level.includes('Nivå 3') || level.includes('Senior')) return 'bg-green-500';
  if (level.includes('Level 2') || level.includes('Nivel 2') || level.includes('Nivå 2') || level.includes('Experienced')) return 'bg-yellow-500';
  return 'bg-orange-500'; // Level 1
};

// Calculate level from score (recalculate based on current thresholds)
const calculateLevelFromScore = (totalScore) => {
  for (const threshold of LEVEL_THRESHOLDS) {
    if (totalScore >= threshold.min) {
      return threshold.level;
    }
  }
  return 'Level 1 (Junior)';
};

// Get level color from score
const getLevelColorFromScore = (totalScore) => {
  for (const threshold of LEVEL_THRESHOLDS) {
    if (totalScore >= threshold.min) {
      return threshold.color;
    }
  }
  return 'bg-orange-500';
};

// Score color
const getScoreColor = (percentage) => {
  if (percentage >= 80) return 'text-green-400';
  if (percentage >= 60) return 'text-yellow-400';
  if (percentage >= 40) return 'text-orange-400';
  return 'text-red-400';
};

function HoldingQuizResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expandedLevels, setExpandedLevels] = useState({});

  // Toggle expand for level card
  const toggleExpandLevel = (level) => {
    setExpandedLevels(prev => ({ ...prev, [level]: !prev[level] }));
  };

  // Fetch results
  const fetchResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/quiz/results`);
      setResults(res.data || []);
    } catch (err) {
      console.error('Error fetching quiz results:', err);
      toast.error('Failed to load quiz results');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  // Filter results
  const filteredResults = results.filter(r => {
    const search = searchTerm.toLowerCase();
    return (
      r.employee_name?.toLowerCase().includes(search) ||
      r.employee_email?.toLowerCase().includes(search) ||
      r.recommended_level?.toLowerCase().includes(search) ||
      r.project_site?.toLowerCase().includes(search)
    );
  });

  // Allow retake (delete result)
  const handleAllowRetake = async () => {
    if (!selectedResult) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/quiz/results/${selectedResult._id}/allow-retake`);
      toast.success(`${selectedResult.employee_name} can now retake the quiz`);
      setShowDeleteDialog(false);
      setSelectedResult(null);
      fetchResults();
    } catch (err) {
      console.error('Error allowing retake:', err);
      toast.error('Failed to allow retake');
    } finally {
      setDeleting(false);
    }
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ['Name', 'Email', 'Phone', 'Site', 'Level', 'Score', 'Percentage', 'Date'];
    const rows = results.map(r => [
      r.employee_name,
      r.employee_email,
      r.employee_phone || '',
      r.project_site || '',
      r.recommended_level,
      `${r.total_score}/${r.max_total}`,
      `${r.total_percentage}%`,
      new Date(r.submitted_at).toLocaleString()
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz_results_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Results exported to CSV');
  };

  // Stats - recalculate levels based on current thresholds
  const stats = {
    total: results.length,
    avgScore: results.length ? Math.round(results.reduce((sum, r) => sum + (r.total_percentage || 0), 0) / results.length) : 0,
    levels: results.reduce((acc, r) => {
      const level = calculateLevelFromScore(r.total_score);
      acc[level] = (acc[level] || 0) + 1;
      return acc;
    }, {})
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="quiz-results-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Quiz Results</h1>
            <p className="text-white/60">View and manage scaffolder level quiz submissions</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchResults} disabled={loading} className="border-white/20">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={results.length === 0} className="border-white/20">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#23F7DD]/10 rounded-lg">
                  <Users className="w-6 h-6 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Total Submissions</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Average Score</p>
                  <p className={`text-2xl font-bold ${getScoreColor(stats.avgScore)}`}>{stats.avgScore}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/5 border-white/10 md:col-span-2">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500/10 rounded-lg">
                  <Award className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white/60 text-sm mb-2">Level Distribution</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.levels).map(([level, count]) => (
                      <Badge key={level} className={`${getLevelColor(level)} text-white`}>
                        {level.replace('Scaffolder ', '').replace('Stilasist ', '')}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Level Thresholds Legend */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Info className="w-5 h-5 text-[#23F7DD]" />
              Level Thresholds & Responsibilities (Max: 184 points)
            </CardTitle>
            <CardDescription>Score requirements and role descriptions for each level</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {LEVEL_THRESHOLDS.map((threshold) => (
                <div key={threshold.level} className={`p-4 rounded-lg ${threshold.color}/10 border border-white/10`}>
                  {/* Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${threshold.color}`}></div>
                      <span className="text-white font-bold">{threshold.level}</span>
                    </div>
                    <span className="text-[#23F7DD] font-bold text-sm">{threshold.min}–{threshold.max} pts</span>
                  </div>
                  
                  {/* Short Description */}
                  <p className="text-white/70 text-sm mb-3 italic">{threshold.shortDesc}</p>
                  
                  {/* Principle (for TL) */}
                  {threshold.principle && (
                    <div className="bg-blue-500/20 rounded p-2 mb-3">
                      <p className="text-blue-300 text-xs font-medium">{threshold.principle}</p>
                    </div>
                  )}
                  
                  {/* Responsibilities */}
                  <div className="mb-2">
                    <p className="text-white/50 text-xs font-medium mb-1">Responsabilități:</p>
                    <ul className="space-y-0.5">
                      {(expandedLevels[threshold.level] 
                        ? threshold.responsibilities 
                        : threshold.responsibilities?.slice(0, 4)
                      )?.map((r, i) => (
                        <li key={i} className="text-white/60 text-xs flex items-start gap-1">
                          <span className="text-green-400 mt-0.5">•</span>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                    {threshold.responsibilities?.length > 4 && (
                      <button 
                        onClick={() => toggleExpandLevel(threshold.level)}
                        className="text-[#23F7DD] text-xs mt-1 hover:underline flex items-center gap-1"
                      >
                        {expandedLevels[threshold.level] ? (
                          <>▲ Arată mai puțin</>
                        ) : (
                          <>▼ +{threshold.responsibilities.length - 4} mai mult...</>
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Accountable For */}
                  {threshold.accountableFor && (
                    <div className="mb-2">
                      <p className="text-white/50 text-xs font-medium mb-1">Răspunde pentru:</p>
                      <ul className="space-y-0.5">
                        {threshold.accountableFor.map((a, i) => (
                          <li key={i} className="text-amber-400/80 text-xs flex items-start gap-1">
                            <span>⚡</span>
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Not Allowed */}
                  {threshold.notAllowed && (
                    <div>
                      <p className="text-white/50 text-xs font-medium mb-1">Nu are dreptul să:</p>
                      <ul className="space-y-0.5">
                        {threshold.notAllowed.map((n, i) => (
                          <li key={i} className="text-red-400/80 text-xs flex items-start gap-1">
                            <span>✗</span>
                            <span>{n}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Note */}
                  {threshold.note && (
                    <p className="text-white/40 text-xs mt-2 italic border-t border-white/10 pt-2">{threshold.note}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <Input
            placeholder="Search by name, email, level, or site..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-white/10"
          />
        </div>

        {/* Results Table */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-0">
            <div className="rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-white/5">
                    <TableHead className="text-white/70">Employee</TableHead>
                    <TableHead className="text-white/70">Contact</TableHead>
                    <TableHead className="text-white/70">Level</TableHead>
                    <TableHead className="text-white/70">Score</TableHead>
                    <TableHead className="text-white/70">Date</TableHead>
                    <TableHead className="text-white/70 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-white/40">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading results...
                      </TableCell>
                    </TableRow>
                  ) : filteredResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-white/40">
                        {searchTerm ? 'No results match your search' : 'No quiz submissions yet'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredResults.map((result) => {
                      // Recalculate level based on current thresholds
                      const calculatedLevel = calculateLevelFromScore(result.total_score);
                      const calculatedColor = getLevelColorFromScore(result.total_score);
                      
                      return (
                      <TableRow key={result._id} className="border-white/10 hover:bg-white/5">
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{result.employee_name}</p>
                            <p className="text-white/50 text-sm">{result.project_site || 'No site specified'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-white/80 text-sm">{result.employee_email}</p>
                            <p className="text-white/50 text-sm">{result.employee_phone || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${calculatedColor} text-white`}>
                            {calculatedLevel}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${getScoreColor(result.total_percentage)}`}>
                              {result.total_percentage}%
                            </span>
                            <span className="text-white/40 text-sm">
                              ({result.total_score}/{result.max_total})
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-white/60 text-sm">
                            {new Date(result.submitted_at).toLocaleDateString()}
                          </p>
                          <p className="text-white/40 text-xs">
                            {new Date(result.submitted_at).toLocaleTimeString()}
                          </p>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedResult(result);
                                setShowDetailDialog(true);
                              }}
                              className="text-[#23F7DD] hover:text-[#1de0c9] hover:bg-[#23F7DD]/10"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedResult(result);
                                setShowDeleteDialog(true);
                              }}
                              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                              title="Allow Retake"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="bg-[#1a1a1a] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#23F7DD]" />
                Quiz Result Details
              </DialogTitle>
              <DialogDescription>
                Detailed breakdown of {selectedResult?.employee_name}'s quiz submission
              </DialogDescription>
            </DialogHeader>
            
            {selectedResult && (
              <div className="space-y-6 py-4">
                {/* Employee Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white/50 text-sm">Name</p>
                    <p className="text-white font-medium">{selectedResult.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Email</p>
                    <p className="text-white">{selectedResult.employee_email}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Phone</p>
                    <p className="text-white">{selectedResult.employee_phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Project/Site</p>
                    <p className="text-white">{selectedResult.project_site || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Submitted</p>
                    <p className="text-white">{new Date(selectedResult.submitted_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Language</p>
                    <p className="text-white uppercase">{selectedResult.language || 'en'}</p>
                  </div>
                </div>

                {/* Overall Score - recalculated */}
                {(() => {
                  const recalculatedLevel = calculateLevelFromScore(selectedResult.total_score);
                  const recalculatedColor = getLevelColorFromScore(selectedResult.total_score);
                  return (
                    <div className={`p-6 rounded-lg text-center ${recalculatedColor}/10 border border-white/10`}>
                      <p className="text-white/60 text-sm mb-1">Recommended Level (Current Thresholds)</p>
                      <p className="text-2xl font-bold text-white mb-2">{recalculatedLevel}</p>
                      <p className={`text-xl font-bold ${getScoreColor(selectedResult.total_percentage)}`}>
                        {selectedResult.total_score}/{selectedResult.max_total} ({selectedResult.total_percentage}%)
                      </p>
                    </div>
                  );
                })()}

                {/* Section Breakdown */}
                <div>
                  <h4 className="text-white font-medium mb-3">Section Breakdown</h4>
                  <div className="space-y-2">
                    {selectedResult.section_scores?.map((section) => (
                      <div key={section.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                        <div className="w-8 h-8 bg-[#23F7DD]/10 rounded flex items-center justify-center text-[#23F7DD] font-bold text-sm">
                          {String(section.id).padStart(2, '0')}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-white/80 text-sm">{section.title}</span>
                            <span className={`font-medium ${getScoreColor(section.percentage)}`}>
                              {section.score}/{section.max} ({section.percentage}%)
                            </span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                section.percentage >= 70 ? 'bg-green-500' :
                                section.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${section.percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Detailed Answers */}
                {selectedResult.answers && Object.keys(selectedResult.answers).length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#23F7DD]" />
                      Individual Answers (All Options)
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(
                        // Group answers by section
                        Object.entries(selectedResult.answers).reduce((acc, [questionId, value]) => {
                          const questionInfo = QUESTIONS_MAP[questionId];
                          if (questionInfo) {
                            const section = questionInfo.section;
                            if (!acc[section]) acc[section] = [];
                            acc[section].push({ questionId, value, ...questionInfo });
                          }
                          return acc;
                        }, {})
                      ).map(([section, questions]) => (
                        <div key={section} className="bg-white/5 rounded-lg overflow-hidden">
                          <div className="bg-white/5 px-4 py-2 border-b border-white/10">
                            <p className="text-white/70 font-medium text-sm">{section}</p>
                          </div>
                          <div className="p-3 space-y-4">
                            {questions.map(({ questionId, value, question, options }) => {
                              const maxValue = Math.max(...Object.keys(options).map(Number));
                              const sortedOptions = Object.entries(options).sort((a, b) => Number(b[0]) - Number(a[0]));
                              
                              return (
                                <div key={questionId} className="border-b border-white/5 pb-3 last:border-0 last:pb-0">
                                  <p className="text-white font-medium text-sm mb-2">{question}</p>
                                  <div className="space-y-1.5 ml-2">
                                    {sortedOptions.map(([pts, label]) => {
                                      const ptsNum = Number(pts);
                                      const isSelected = ptsNum === value;
                                      const isMax = ptsNum === maxValue;
                                      const isZero = ptsNum === 0;
                                      
                                      return (
                                        <div 
                                          key={pts} 
                                          className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
                                            isSelected 
                                              ? isMax 
                                                ? 'bg-green-500/20 border border-green-500/40' 
                                                : isZero 
                                                  ? 'bg-red-500/20 border border-red-500/40'
                                                  : 'bg-yellow-500/20 border border-yellow-500/40'
                                              : 'bg-white/3 border border-transparent'
                                          }`}
                                        >
                                          {/* Radio indicator */}
                                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                            isSelected 
                                              ? isMax ? 'border-green-400' : isZero ? 'border-red-400' : 'border-yellow-400'
                                              : 'border-white/30'
                                          }`}>
                                            {isSelected && (
                                              <div className={`w-2 h-2 rounded-full ${
                                                isMax ? 'bg-green-400' : isZero ? 'bg-red-400' : 'bg-yellow-400'
                                              }`} />
                                            )}
                                          </div>
                                          
                                          {/* Option text */}
                                          <span className={`flex-1 text-sm ${
                                            isSelected 
                                              ? isMax ? 'text-green-400 font-medium' : isZero ? 'text-red-400 font-medium' : 'text-yellow-400 font-medium'
                                              : 'text-white/50'
                                          }`}>
                                            {label}
                                          </span>
                                          
                                          {/* Points badge */}
                                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                            isSelected
                                              ? isMax ? 'bg-green-500/30 text-green-300' : isZero ? 'bg-red-500/30 text-red-300' : 'bg-yellow-500/30 text-yellow-300'
                                              : isMax ? 'bg-green-500/10 text-green-400/60' : isZero ? 'bg-red-500/10 text-red-400/60' : 'bg-white/5 text-white/40'
                                          }`}>
                                            {pts} pts
                                          </span>
                                          
                                          {/* Selected indicator */}
                                          {isSelected && (
                                            <span className={`text-xs font-bold ${
                                              isMax ? 'text-green-400' : isZero ? 'text-red-400' : 'text-yellow-400'
                                            }`}>
                                              ✓ ALES
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments */}
                {selectedResult.comments && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 font-medium mb-1">Comments</p>
                    <p className="text-white/80">{selectedResult.comments}</p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="border-white/20">
                Close
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDetailDialog(false);
                  setShowDeleteDialog(true);
                }}
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Allow Retake
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete/Allow Retake Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-[#1a1a1a] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-amber-400" />
                Allow Quiz Retake
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to allow <strong className="text-white">{selectedResult?.employee_name}</strong> to retake the quiz?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm">
                  <strong>Warning:</strong> This will delete the current quiz result for{' '}
                  <span className="text-white">{selectedResult?.employee_email}</span>.
                  The employee will be able to take the quiz again.
                </p>
              </div>
              
              {selectedResult && (
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <p className="text-white/60 text-sm">Current Result:</p>
                  <p className="text-white font-medium">{selectedResult.recommended_level}</p>
                  <p className="text-white/60">{selectedResult.total_percentage}% ({selectedResult.total_score}/{selectedResult.max_total})</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-white/20">
                Cancel
              </Button>
              <Button
                onClick={handleAllowRetake}
                disabled={deleting}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Allow Retake
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default HoldingQuizResults;
